import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavIcon } from '../../../shell/shared';

export interface DeleteInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (opts: { deletePayments: boolean; deleteTreatmentRecords: boolean }) => void;
  invoiceNo: string;
  isPaid: boolean;
  hasPayments: boolean;
  hasTreatmentRecords: boolean;
  isDeleting: boolean;
}

export function DeleteInvoiceModal({
  open,
  onClose,
  onConfirm,
  invoiceNo,
  isPaid,
  hasPayments,
  hasTreatmentRecords,
  isDeleting,
}: DeleteInvoiceModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [deletePayments, setDeletePayments] = useState(true);
  const [deleteTreatmentRecords, setDeleteTreatmentRecords] = useState(true);

  useEffect(() => {
    if (open) {
      setConfirmText('');
      setDeletePayments(hasPayments || isPaid);
      setDeleteTreatmentRecords(hasTreatmentRecords);
    }
  }, [open, hasPayments, isPaid, hasTreatmentRecords]);

  const isConfirmed = confirmText.toUpperCase() === 'DELETE';

  const handleConfirm = useCallback(() => {
    if (!isConfirmed || isDeleting) return;
    onConfirm({ deletePayments, deleteTreatmentRecords });
  }, [isConfirmed, isDeleting, onConfirm, deletePayments, deleteTreatmentRecords]);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={isDeleting ? undefined : onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
      />
      <div
        style={{
          position: 'relative',
          width: 460,
          maxWidth: '90vw',
          borderRadius: 'var(--radius-lg, 14px)',
          background: 'var(--bg-surface, #fff)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md, 10px)',
                background: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#dc2626',
              }}
            >
              <NavIcon name="trash" />
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg, 18px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                color: 'var(--text, #111827)',
              }}
            >
              Delete invoice {invoiceNo}?
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-sm, 6px)',
              border: 'none',
              background: 'none',
              cursor: isDeleting ? 'default' : 'pointer',
              color: 'var(--text-muted, #6b7280)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 13px)',
              color: 'var(--text-muted, #6b7280)',
              lineHeight: 1.5,
            }}
          >
            This will permanently delete the invoice and its line items. This cannot be undone.
          </p>

          {(hasPayments || hasTreatmentRecords) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 'var(--radius-md, 10px)',
                background: 'var(--bg-surface-2, #f9fafb)',
                border: '1px solid var(--border, #e5e7eb)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  fontWeight: 'var(--font-weight-semi, 600)',
                  color: 'var(--text-muted, #6b7280)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Also delete:
              </span>
              {hasPayments && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 'var(--font-size-sm, 13px)',
                    color: 'var(--text, #111827)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={deletePayments}
                    onChange={(e) => setDeletePayments(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#dc2626' }}
                  />
                  Linked Payments
                </label>
              )}
              {hasTreatmentRecords && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 'var(--font-size-sm, 13px)',
                    color: 'var(--text, #111827)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={deleteTreatmentRecords}
                    onChange={(e) => setDeleteTreatmentRecords(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#dc2626' }}
                  />
                  Linked Treatment Records
                </label>
              )}
            </div>
          )}

          <div>
            <label
              style={{
                fontSize: 'var(--font-size-sm, 13px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                color: 'var(--text, #111827)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Type <strong>DELETE</strong> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={isDeleting}
              autoFocus
              style={{
                width: '100%',
                height: 'var(--input-height, 42px)',
                padding: '0 var(--input-padding-x, 16px)',
                borderRadius: 'var(--input-radius, 10px)',
                border: `1px solid ${isConfirmed ? '#dc2626' : 'var(--border, #e5e7eb)'}`,
                background: 'var(--input-bg, #fff)',
                fontSize: 'var(--font-size-base, 14px)',
                color: 'var(--input-text, #0a0a0a)',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            borderTop: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <button
            onClick={onClose}
            disabled={isDeleting}
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid var(--border, #e5e7eb)',
              background: 'var(--bg-surface, #fff)',
              color: 'var(--text, #111827)',
              fontSize: 'var(--font-size-sm, 13px)',
              fontWeight: 'var(--font-weight-medium, 500)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmed || isDeleting}
            style={{
              height: 38,
              padding: '0 20px',
              borderRadius: 'var(--radius-md, 10px)',
              border: 'none',
              background: isConfirmed && !isDeleting ? '#dc2626' : '#e5e7eb',
              color: isConfirmed && !isDeleting ? '#fff' : '#9ca3af',
              fontSize: 'var(--font-size-sm, 13px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              cursor: isConfirmed && !isDeleting ? 'pointer' : 'default',
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Invoice'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
